<template>
  <div class="item-row" :class="{ 'rec-row-zone': isPersonalized }" ref="rowRef">
    <template v-if="totalCount > 0">
      <div :class="productRowClass">
        <div v-if="loadFailedError">{{ loadFailedError }}</div>
        <div v-if="!loadFailedError" class="item-row-heading">
          <h2 class="wc-coupon-row-modern__title">
            <span class="wc-coupon-row-modern__title-text">{{ rowTitle }}</span>
          </h2>
          <span v-if="isPersonalized" class="wc-coupon-row-modern__badge">{{
            t('personalized')
          }}</span>
          <router-link
            v-if="showViewMoreButton"
            :to="viewMoreLink"
            class="wc-coupon-row-modern__view-more-link"
            data-testid="viewMoreButton"
          >
            {{ t('seeAll') }}
          </router-link>
        </div>
        <template v-if="!loadFailedError">
          <div v-if="loading" class="wc-item-row__spinner-wrapper d-flex">
            <WCSpinner class="mx-auto align-self-center" size="xl" />
          </div>
          <template v-if="!loading">
            <WCCarouselModern :title="rowTitle" :showArrows="true" :class="productRowCarouselClass">
              <WCDigitalCouponCardModern
                v-for="coupon in coupons"
                :key="coupon.id"
                :coupon="coupon"
                class="wc-coupon-row-modern__card"
                role="listitem"
              />
            </WCCarouselModern>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, onMounted, ref, withDefaults } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCCarouselModern from '@/components/WCCarouselModern/WCCarouselModern.vue';
import WCDigitalCouponCardModern from '@/components/WCDigitalCouponCardModern/WCDigitalCouponCardModern.vue';
import useCouponAvailability from '@/modules/coupons/composables/useCouponAvailability';
import useCoupon from '@/modules/coupons/composables/useCoupon';
import usePlatform from '@/modules/platform/composables/usePlatform';
import useCardLimitInRow from '@/composables/useCardLimitInRow';
import ExternalCoupon from '@/models/entities/items/external-coupon';

interface Props {
  useRecs?: boolean;
  isPersonalized?: boolean;
  rowSizeLimit?: number;
}

const props = withDefaults(defineProps<Props>(), {
  useRecs: false,
  isPersonalized: false,
  rowSizeLimit: 8,
});

const emit = defineEmits(['noRecs']);

const { t } = useI18n();
const { couponDisplayable } = useCouponAvailability();
const { platform } = usePlatform();
const { fetchCustomerCoupons } = useCoupon({} as ExternalCoupon);

const loading = ref(false);
const loadFailedError = ref<Error | null>(null);
const totalCount = ref(-1);
const coupons = ref<ExternalCoupon[]>([]);
const rowRef = ref<HTMLElement | null>(null);

const getCardLimitInRow = useCardLimitInRow(rowRef, () => props.rowSizeLimit);

const rowTitle = computed(() => (props.useRecs ? t('couponsForYou') : t('digitalCoupons')));
const productRowClass = platform.value.isMobile
  ? 'coupon-row-class-mobile'
  : 'coupon-row-class-desktop';
const productRowCarouselClass = platform.value.isMobile
  ? 'wc-coupon-row-modern__carousel-mobile'
  : 'wc-coupon-row-modern__carousel-desktop';
const viewMoreLink = computed(() => '/cpb');
const showViewMoreButton = computed(() => {
  if (props.useRecs) {
    return true;
  }
  return totalCount.value > coupons.value.length;
});

const fetchCoupons = async () => {
  try {
    if (props.useRecs) {
      const response = await axios.get('api/cpb/recs');
      if (response?.data?.data) {
        coupons.value = response.data.data;
        totalCount.value = response.data.data.length;
      }

      if (totalCount.value <= 0) {
        emit('noRecs');
      }
    } else {
      const ps = platform.value.isMobile ? props.rowSizeLimit : getCardLimitInRow();
      const response = await axios.post('api/cpb/', { ps });
      if (response.data) {
        coupons.value = response.data.items?.filter(i => couponDisplayable(i));
        totalCount.value = response.data.totalCount;
      }
    }
  } catch (error) {
    loading.value = false;
    loadFailedError.value = error;
    console.error(error);

    if (props.useRecs) {
      emit('noRecs');
    }
  }
};

const updateData = async () => {
  loading.value = true;
  loadFailedError.value = null;
  await Promise.all([fetchCoupons(), fetchCustomerCoupons()]);
  loading.value = false;
};

onMounted(() => {
  updateData();
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-item-row__spinner-wrapper {
  height: 12.5rem;
}

.wc-coupon-row-modern__card {
  width: convert-px-to-rem(150);
  flex-shrink: 0;
}

.wc-coupon-row-modern__title {
  font-size: $font-size-2xl;
  font-weight: 700;
  color: var(--dark, $dark);
  line-height: 1.2;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wc-coupon-row-modern__badge {
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: var(--white, $white);
  background: var(--info);
  border-radius: 0.25rem;
  padding: 0.0625rem 0.375rem;
  margin-left: 0.375rem;
  flex-shrink: 0;
}

.wc-coupon-row-modern__view-more-link {
  font-size: $font-size-sm;
  font-weight: 400;
  color: var(--info);
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 1rem;
}

.item-row {
  .coupon-row-class-desktop {
    padding: 0;
    margin-top: 0.875rem;
  }
  .coupon-row-class-mobile {
    padding: 0;
    margin-top: 0.25rem;
    .item-row-heading {
      padding: 1rem;
    }
  }
  .item-row-heading {
    display: flex;
    align-items: center;
    padding-top: 1rem;
    width: 100%;
  }
}

.wc-coupon-row-modern__carousel-desktop {
  :deep(.wc-carousel-modern__track) {
    padding: 0 1rem 0 0 !important;
    margin: 0 !important;
    scroll-padding-left: 0 !important;
  }

  @media (hover: hover) and (pointer: fine) {
    :deep(.wc-carousel-modern__track) {
      padding-top: 1.0625rem !important;
      padding-bottom: 0rem !important;
    }
  }
}

.wc-coupon-row-modern__carousel-mobile {
  :deep(.wc-carousel-modern__track) {
    padding: 0.0625rem 1rem 0.0625rem 1rem !important;
    margin: 0 !important;
  }
}
</style>
